/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { colors, sizes, Spacer, TypeRampV2 } from '@smartsheet/lodestar-core';
import { ArrowRightIcon } from '@smartsheet/lodestar-icons';
import React, { useEffect, useState } from 'react';
import useCollapse from 'react-collapsed';
import { useDispatch } from 'react-redux';
import {
  DraggableNodeTypes,
  GraphNodeTypes,
} from '../../../../../services/ducks/designerDuck/designerDuck';
import { RemoteImage } from '../../../../common/RemoteImage/RemoteImage';
import { ChildWorkflowIcon } from '../../../../icons/ChildWorkflowIcon';
import { JunctionIcon } from '../../../../icons/JunctionIcon';
import { StateIcon } from '../../../../icons/StateIcon';
import {
  getDragEndHandler,
  getDragStartHandler,
  onDragHandler,
} from '../../../Canvas/Nodes/utils';
import { generateUUIDForNewNode } from '../utils';

interface AddModuleListItemProps {
  children?: React.ReactNode;
  logoName?: string;
  label: string;
  isOpen?: boolean;
  moduleID?: string;
  extensionUUID?: string;
  type: DraggableNodeTypes;
}

export const AddModuleListItem = ({
  children,
  logoName,
  label,
  isOpen = false,
  moduleID,
  extensionUUID,
  type,
}: AddModuleListItemProps) => {
  const [isExpanded, setIsExpanded] = useState(isOpen);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });
  const dispatch = useDispatch();

  useEffect(() => {
    setIsExpanded(isOpen);
  }, [isOpen]);

  const icon = logoName?.split('.')[0] + '.svg';

  const style = css`
    box-sizing: border-box;
    height: ${sizes.xLarge}px;
    line-height: ${sizes.large}px;
    padding: ${sizes.xxSmall}px 0;
    cursor: ${!children ? 'grab' : 'default'};
    &:hover {
      background-color: ${colors.neutralLight20};
      border-radius: 2px;
    }
  `;

  const caretStyle = css`
    transition: transform 200ms;
    ${isExpanded ? 'transform: rotate(90deg)' : null}
  `;

  const iconStyle = css`
    margin: auto 0;
    padding: 2px ${sizes.small}px;
  `;

  const labelStyle = css`
    line-height: ${sizes.large}px !important;
    ${!children &&
    (type === GraphNodeTypes.MODULE || type === GraphNodeTypes.GROUP_JUNCTION)
      ? `padding-left: ${sizes.xLarge}px`
      : null}
  `;

  return (
    <React.Fragment>
      <Spacer
        orientation="row"
        space="small"
        css={style}
        draggable={children ? false : true}
        onDragStart={getDragStartHandler(dispatch, type, {
          id: generateUUIDForNewNode(),
          __type: 'new',
          moduleID,
          pluginUUID: extensionUUID,
          type,
          next: [],
        })}
        onDrag={onDragHandler}
        onDragEnd={getDragEndHandler(dispatch)}
        {...getToggleProps({
          onClick: () => {
            if (children) setIsExpanded(prevExpanded => !prevExpanded);
          },
        })}
      >
        {children ? <ArrowRightIcon css={caretStyle} /> : null}
        {type === GraphNodeTypes.STATE ? (
          <div css={iconStyle}>
            <StateIcon />
          </div>
        ) : null}
        {type === GraphNodeTypes.JUNCTION ? (
          <div css={iconStyle}>
            <JunctionIcon />
          </div>
        ) : null}
        {type === GraphNodeTypes.CHILD ? (
          <div css={iconStyle}>
            <ChildWorkflowIcon />
          </div>
        ) : null}
        {logoName ? (
          <RemoteImage
            url={`https://assets.bridge.smartsheet.com/images/module-logos/${icon}`}
            style={{ margin: 5, maxHeight: 14, maxWidth: 14 }}
            altText={`${label} logo`}
          />
        ) : null}
        <TypeRampV2 system="lodestar" variant="bodyText" css={labelStyle}>
          {label}
        </TypeRampV2>
      </Spacer>
      <Spacer orientation="column" space="xxSmall" {...getCollapseProps()}>
        {children}
      </Spacer>
    </React.Fragment>
  );
};
